import { useMemo } from "react";
import type { Level, Metric, Segment } from "@/modules/product/types";

interface UseFilteredProductHierarchyProps {
    selectedProductIds: number[];
    selectedLevelId: number | null;
    servicesByProductId: Record<number, number[]>;
    levels: Level[];
    segments: Segment[];
    metrics: Metric[];
}

interface UseFilteredProductHierarchyReturn {
    availableLevels: Level[];
    availableSegments: Segment[];
    availableMetrics: Metric[];
}

/**
 * Hook to filter product hierarchy data based on selected products and levels.
 * Returns only the intersection of levels and metrics that exist across ALL selected products.
 * Segments are filtered by the selected level.
 */
export const useFilteredProductHierarchy = ({
    selectedProductIds,
    selectedLevelId,
    servicesByProductId,
    levels,
    segments,
    metrics,
}: UseFilteredProductHierarchyProps): UseFilteredProductHierarchyReturn => {
    const availableLevels = useMemo(() => {
        if (!selectedProductIds.length) {
            return [];
        }

        // Get levels for each selected product
        const levelsByProduct = selectedProductIds.map((productId) => {
            const serviceIds = new Set(servicesByProductId[productId] ?? []);
            return levels.filter((level) => serviceIds.has(level.serviceId));
        });

        // Find intersection: levels with matching names that exist in ALL selected products
        if (levelsByProduct.length === 0) return [];

        return levelsByProduct[0].filter((level) => levelsByProduct.every((productLevels) => productLevels.some((l) => l.name === level.name)));
    }, [levels, selectedProductIds, servicesByProductId]);

    const availableSegments = useMemo(() => {
        if (!selectedLevelId) {
            return [];
        }
        return segments.filter((segment) => segment.levelId === selectedLevelId);
    }, [segments, selectedLevelId]);

    const availableMetrics = useMemo(() => {
        if (!selectedProductIds.length || !selectedLevelId) {
            return [];
        }

        // Get metrics for each selected product
        const metricsByProduct = selectedProductIds.map((productId) => {
            const serviceIds = new Set(servicesByProductId[productId] ?? []);
            return metrics.filter((metric) => serviceIds.has(metric.serviceId));
        });

        // Find intersection: metrics with matching names that exist in ALL selected products
        if (metricsByProduct.length === 0) return [];

        return metricsByProduct[0].filter((metric) => metricsByProduct.every((productMetrics) => productMetrics.some((m) => m.name === metric.name)));
    }, [metrics, selectedProductIds, selectedLevelId, servicesByProductId]);

    return {
        availableLevels,
        availableSegments,
        availableMetrics,
    };
};
